/**
 * Mobile menu items (in particular a list-like menu with potentially nested submenus)
 *
 * This is a pretty funky implementation.  Like...imagine if you had a tab
 * menu, but there are also tabs inside the individual tab displays that pull
 * up more tab displays that might have tabs ad infinitum.
 *
 * That's this.
 *
 * For the record, this isn't actually optimized for ad infinitum.  It's not
 * optimized at all.  We could, but.  Like.  Stop nesting menus.
 */

"use client";

import {memo, useCallback, useState} from "react";
import {FocusScope, useKeyboard} from "react-aria";
import {FCWithHTMLAttributes} from "../../../types/reactTypes";
import clsMerge from "../../../util/clsMerge";
import {CollapsibleMenu, MenuItem} from "./MenuTypes";
import MobileMenuItem from "./MobileMenuItem";
import MobileSubmenuItem from "./MobileSubmenu";
import {AnimatePresence, useAnimate, useReducedMotion} from "framer-motion";

type MobileMenuItemsProps = {
	menuLabel: string;
	items: MenuItem[];
};

const MobileMenuItems: FCWithHTMLAttributes<MobileMenuItemsProps> = ({
	className,
	items,
	menuLabel,
}) => {
	const [activeSubmenus, setActiveSubmenus] = useState<CollapsibleMenu[]>([]);

	/**
	 * animation hook specifically for the home item in the main menu layout as
	 * it animates differently (ngl most of them don't animate).
	 * Home animates up/down when it's replaced with the back button.
	 **/
	const [home, animateHome] = useAnimate();
	const shouldReduceMotion = useReducedMotion();

	const openSubmenu = useCallback(
		(submenu: CollapsibleMenu) => {
			setActiveSubmenus((currentlyOpen) => {
				if (!shouldReduceMotion && !currentlyOpen.length) {
					animateHome(home.current, {top: "0.5rem"});
				}
				return [...currentlyOpen, submenu];
			});
		},
		[animateHome, shouldReduceMotion, home]
	);

	const closeSubmenu = useCallback(() => {
		/**
		 * this is a somewhat dangerous thing to do, but assuming that close
		 * was triggered on the most recently pushed id is fair and probably
		 * more optimal than filter.
		 */
		setActiveSubmenus((currentlyOpen) => {
			if (!shouldReduceMotion && currentlyOpen.length === 1) {
				animateHome(home.current, {top: 0});
			}
			return currentlyOpen.slice(0, -1);
		});
	}, [home, shouldReduceMotion, animateHome]);

	const closeAllSubmenus = () => {
		setActiveSubmenus(() => []);
		animateHome(home.current, {top: 0});
	};

	const {keyboardProps} = useKeyboard({
		onKeyDown: (e) => {
			switch (e.key) {
				case "Escape":
					if (activeSubmenus.length > 0) {
						closeAllSubmenus();
					} else {
						e.continuePropagation();
					}
					break;
			}
		},
	});

	return (
		<FocusScope>
			<div
				className={clsMerge(
					"relative flex h-full w-full flex-col overflow-auto",
					className
				)}
				role="menu"
				aria-label={menuLabel}
				{...keyboardProps}
			>
				<div
					className={clsMerge(
						"flex flex-col md:items-center",
						activeSubmenus.length > 0 && "max-h-full overflow-hidden"
					)}
				>
					{/** Always create Home item, always put at top.  Item also animates differently */}
					<div className="relative overflow-hidden p-0">
						<MobileMenuItem
							className="relative"
							ref={home}
							href="/"
							id="Home"
							label="Home"
							openSubmenu={openSubmenu}
						/>
					</div>
					{items.map((item) => {
						return (
							<MobileMenuItem
								key={item.id}
								openSubmenu={openSubmenu}
								isExpanded={
									activeSubmenus.length > 0 &&
									activeSubmenus[activeSubmenus.length - 1].id === item.id
								}
								{...item}
							/>
						);
					})}
				</div>
				<AnimatePresence>
					{activeSubmenus.map((submenu, index, {length}) => {
						/**
						 * Assuming the submenus appear in a stack, the only case
						 * in which we care about the current expanded item is in
						 * the second top top-most submenu.
						 * By doing so we can guarantee the props change iif they
						 * need to
						 */
						const expandedItem =
							length > 1 && index === length - 2
								? activeSubmenus[length - 1].id
								: "";

						return (
							<FocusScope key={submenu.id} restoreFocus contain>
								<MobileSubmenuItem
									expandedItem={expandedItem}
									openSubmenu={openSubmenu}
									onClose={closeSubmenu}
									{...submenu}
								/>
							</FocusScope>
						);
					})}
				</AnimatePresence>
			</div>
		</FocusScope>
	);
};

export default memo(MobileMenuItems);
